import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/marketing-hub-ui';
import React, { useEffect, useState } from 'react';
import {
    EventDetailsSection,
    EventDetailsSectionTitle,
} from '../EventDetails/EventDetails';
import {
    generateCuratedCategory,
    getEventCuratedCategory,
    saveEventCuratedCategory,
    saveEventIdInCuratedCategory,
} from './api';
import './CuratedCategory.scss';
import { CURATED_CATEGORY_TAG_MAP } from './models';
import {
    getCuratedCategoryTag,
    getCuratedCategoryText,
    sanitizeText,
} from './utils';

type CuratedCategoryProps = {
    eventId: string;
    eventUrl: string;
};

export const CuratedCategory = ({
    eventId,
    eventUrl,
}: CuratedCategoryProps) => {
    const [curatedCategoryKey, setCuratedCategoryKey] = useState('');
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    async function askAI() {
        try {
            const categoryKey = await getEventCuratedCategory(eventId);

            if (categoryKey) {
                setCuratedCategoryKey(categoryKey);
                return setLoading(false);
            }

            const generatedCuratedCategoryText = await generateCuratedCategory(
                eventUrl,
            );
            const sanitizedText = sanitizeText(
                generatedCuratedCategoryText.text,
            );
            const newCategoryKey = getCuratedCategoryTag(sanitizedText);
            if (!newCategoryKey) {
                return setLoading(false);
            }

            saveEventCuratedCategory(
                eventId,
                newCategoryKey as keyof typeof CURATED_CATEGORY_TAG_MAP,
            );
            saveEventIdInCuratedCategory(
                eventId,
                newCategoryKey as keyof typeof CURATED_CATEGORY_TAG_MAP,
            );

            setCuratedCategoryKey(newCategoryKey);
        } catch (e) {
            console.error(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        askAI();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (error || (!loading && !curatedCategoryKey)) {
        return null;
    }

    return (
        <EventDetailsSection>
            <EventDetailsSectionTitle>
                <h2>{gettext('This event is for...')}</h2>
            </EventDetailsSectionTitle>
            {loading ? (
                <div className="categories-container">
                    <LoadingSkeleton width="250px" height="48px" />
                </div>
            ) : (
                <div className="categories-container categories-container-image">
                    <a
                        href={`/curated-categories/${curatedCategoryKey}`}
                        className="categories-link"
                    >
                        <p className="categories-text">
                            {getCuratedCategoryText(
                                curatedCategoryKey as keyof typeof CURATED_CATEGORY_TAG_MAP,
                            )}
                        </p>
                    </a>
                </div>
            )}
        </EventDetailsSection>
    );
};
